// src/pages/events/EventEdit.js
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Container,
  Paper,
  Typography,
  TextField,
  Button,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Snackbar,
  Alert,
  FormControlLabel,
  Switch,
  Box,
  CircularProgress,
  Divider,
  Stack,
  Chip,
  Tooltip,
  IconButton,
  InputAdornment
} from '@mui/material';
import {
  Save as SaveIcon,
  Delete as DeleteIcon,
  ArrowBack as ArrowBackIcon,
  Image as ImageIcon,
  Euro as EuroIcon,
  ConfirmationNumber as TicketIcon,
  LocationOn as LocationIcon,
  Link as LinkIcon,
  Person as PersonIcon,
  Phone as PhoneIcon
} from '@mui/icons-material';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider, DateTimePicker } from '@mui/x-date-pickers';
import { es } from 'date-fns/locale';
import { doc, getDoc, updateDoc, deleteDoc, serverTimestamp, Timestamp } from 'firebase/firestore';
import { db } from '../../../firebase';
import { uploadToGarage, useGarageUrl } from '../../../garage';

const EventEdit = () => {
  const { eventId } = useParams();
  const navigate = useNavigate();
  
  // Estados principales
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);

  // Estado del formulario
  const [formData, setFormData] = useState({
    active: true,
    asistentes: [],
    categoria: 'general',
    contacto: '',
    descripcion: '',
    detalles: '',
    direcciones: '',
    enlace: '',
    fechaFin: null,
    fechaInicio: new Date(),
    galeria: [],
    imagen: '',
    organizador: '',
    precio: 0,
    titulo: '',
    ubicacion: '',
    voluntarios: [],
    totalpass: 0,
    sold: 0
  });
  
  // Estado original del evento para comparar cambios
  const [originalEvent, setOriginalEvent] = useState(null);
  
  // Estados para manejo de UI
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });
  
  const [fileUpload, setFileUpload] = useState({
    file: null,
    progress: 0,
    uploading: false
  });

  // Resuelve formData.imagen para el preview:
  // - data:/blob: URL (FileReader) → devuelta tal cual
  // - path de Garage (events/xxx.png) → genera presigned URL
  // - http URL (Firebase legacy) → devuelta tal cual
  const previewSrc = useGarageUrl(formData.imagen);

  // Categorías de eventos
  const categories = [
    { id: 'general', name: 'General' },
    { id: 'cultural', name: 'Cultural' },
    { id: 'deportivo', name: 'Deportivo' },
    { id: 'educativo', name: 'Educativo' },
    { id: 'familiar', name: 'Familiar' },
    { id: 'festivo', name: 'Festivo' }
  ];

  // Cargar evento existente
  useEffect(() => {
    const fetchEvent = async () => {
      if (!eventId) {
        navigate('/admin/events');
        return;
      }

      setLoading(true);
      try {
        const eventRef = doc(db, 'events', eventId);
        const eventDoc = await getDoc(eventRef);
        
        if (eventDoc.exists()) {
          const eventData = eventDoc.data();
          setOriginalEvent(eventData);
          
          // Convertir Timestamps a Date objects para los date pickers
          const formattedData = {
            ...eventData,
            fechaInicio: eventData.fechaInicio?.seconds
              ? new Date(eventData.fechaInicio.seconds * 1000)
              : new Date(),
            fechaFin: eventData.fechaFin?.seconds
              ? new Date(eventData.fechaFin.seconds * 1000)
              : null,
            asistentes: eventData.asistentes || [],
            voluntarios: eventData.voluntarios || [],
            galeria: eventData.galeria || [],
            totalpass: eventData.totalpass || 0,
            sold: eventData.sold || 0
          };
          
          setFormData(formattedData);
        } else {
          showSnackbar('No se encontró el evento', 'error');
          navigate('/admin/events');
        }
      } catch (error) {
        console.error("Error al cargar evento:", error);
        showSnackbar('Error al cargar el evento', 'error');
        navigate('/admin/events');
      } finally {
        setLoading(false);
      }
    };

    fetchEvent();
  }, [eventId, navigate]);

  // Manejar cambios en los campos del formulario
  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    let processedValue = value;
    
    // Manejo especial para el campo precio
    if (name === 'precio') {
      const numericValue = value.replace(/[^0-9.]/g, '');
      const parts = numericValue.split('.');
      if (parts.length > 2) {
        processedValue = parts[0] + '.' + parts.slice(1).join('');
      } else {
        processedValue = numericValue;
      }
    }
    
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : processedValue
    }));
  };

  // Manejar cambios en las fechas
  const handleDateChange = (name, value) => {
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Manejar cambio de archivo de imagen principal
  const handleFileChange = (e) => {
    if (e.target.files[0]) {
      setFileUpload({
        ...fileUpload,
        file: e.target.files[0]
      });
      
      const reader = new FileReader();
      reader.onload = () => {
        setFormData({
          ...formData,
          imagen: reader.result
        });
      };
      reader.readAsDataURL(e.target.files[0]);
    }
  };

  const uploadFile = async () => {
    if (!fileUpload.file) return formData.imagen || '';
    setFileUpload(prev => ({ ...prev, uploading: true, progress: 0 }));
    try {
      const url = await uploadToGarage(fileUpload.file, 'events');
      setFileUpload({ file: null, progress: 100, uploading: false });
      return url;
    } catch (error) {
      console.error("Error en proceso de subida:", error);
      setFileUpload(prev => ({ ...prev, uploading: false, progress: 0 }));
      throw error;
    }
  };

  // Actualizar evento
  const handleUpdateEvent = async (e) => {
    e.preventDefault();

    // Validaciones básicas
    if (!formData.titulo || !formData.descripcion) {
      showSnackbar('Por favor, completa los campos obligatorios', 'error');
      return;
    }

    // Validar totalPass para eventos de pago
    if (Number(formData.precio) > 0 && (!formData.totalpass || Number(formData.totalpass) <= 0)) {
      showSnackbar('Para eventos de pago, debes indicar el número total de entradas disponibles', 'error');
      return;
    }

    // Validar que totalPass no sea menor que sold
    if (Number(formData.totalpass) < Number(formData.sold)) {
      showSnackbar('El total de entradas no puede ser menor que las entradas ya vendidas', 'error');
      return;
    }

    setSaving(true);
    
    try {
      let imageUrl = formData.imagen && formData.imagen.startsWith('data:')
        ? (originalEvent?.imagen || '')
        : (formData.imagen || '');

      // Si hay un nuevo archivo para subir
      if (fileUpload.file) {
        imageUrl = await uploadFile();
        if (!imageUrl) {
          showSnackbar('Error al subir la imagen', 'error');
          setSaving(false);
          return;
        }
      }
      
      // Preparar datos del evento
      const eventData = {
        active: Boolean(formData.active),
        asistentes: formData.asistentes || [],
        categoria: formData.categoria || 'general',
        contacto: formData.contacto || '',
        descripcion: formData.descripcion || '',
        detalles: formData.detalles || '',
        direcciones: formData.direcciones || '',
        enlace: formData.enlace || '',
        fechaFin: formData.fechaFin ? Timestamp.fromDate(formData.fechaFin) : null,
        fechaInicio: formData.fechaInicio ? Timestamp.fromDate(formData.fechaInicio) : Timestamp.fromDate(new Date()),
        galeria: formData.galeria || [],
        imagen: imageUrl,
        organizador: formData.organizador || '',
        precio: Number(formData.precio) || 0,
        titulo: formData.titulo || '',
        ubicacion: formData.ubicacion || '',
        voluntarios: formData.voluntarios || [],
        totalpass: Number(formData.precio) > 0 ? Number(formData.totalpass) || 0 : 0,
        sold: Number(formData.sold) || 0,
        updatedAt: serverTimestamp()
      };
      
      // Actualizar evento
      const eventRef = doc(db, 'events', eventId);
      await updateDoc(eventRef, eventData);
      
      showSnackbar('Evento actualizado correctamente', 'success');
      
      // Redirigir a la lista de eventos después de un breve delay
      setTimeout(() => {
        navigate('/admin/events');
      }, 1500);
      
    } catch (error) {
      console.error("Error al actualizar evento:", error);
      
      let errorMessage = 'Error al actualizar el evento';
      if (error.code === 'permission-denied') {
        errorMessage = 'No tienes permisos para realizar esta acción';
      } else if (error.code === 'network-request-failed') {
        errorMessage = 'Error de conexión. Verifica tu internet';
      } else if (error.message) {
        errorMessage = `Error: ${error.message}`;
      }
      
      showSnackbar(errorMessage, 'error');
    } finally {
      setSaving(false);
    }
  };

  // Eliminar evento
  const handleDeleteEvent = async () => {
    if (window.confirm('¿Estás seguro de que deseas eliminar este evento? Los pagos y registros asociados se mantendrán en el sistema.')) {
      setDeleting(true);

      try {
        await deleteDoc(doc(db, 'events', eventId));
        showSnackbar('Evento eliminado correctamente', 'success');
        
        setTimeout(() => {
          navigate('/admin/events');
        }, 1500);
      } catch (error) {
        console.error("Error al eliminar evento:", error);
        showSnackbar('Error al eliminar el evento', 'error');
        setDeleting(false);
      }
    }
  };

  const showSnackbar = (message, severity) => {
    setSnackbar({
      open: true,
      message,
      severity
    });
  };

  const handleCloseSnackbar = () => {
    setSnackbar({
      ...snackbar,
      open: false
    });
  };

  if (loading) {
    return (
      <Container sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '80vh' }}>
        <CircularProgress />
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 2, mb: 4 }}>

      {/* ── Cabecera ── */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 3 }}>
        <Tooltip title="Volver a eventos">
          <IconButton onClick={() => navigate('/admin/events')} size="small">
            <ArrowBackIcon />
          </IconButton>
        </Tooltip>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="h5" fontWeight={700} noWrap>
            {formData.titulo || 'Editar evento'}
          </Typography>
          <Typography variant="caption" color="text.secondary">Edición de evento</Typography>
        </Box>
      </Box>

      <form onSubmit={handleUpdateEvent}>
        <Grid container spacing={3} alignItems="flex-start">

          {/* ══ COLUMNA PRINCIPAL ══ */}
          <Grid item xs={12} lg={8}>

            {/* Información básica */}
            <Paper sx={{ p: 3, mb: 3, borderRadius: 2 }}>
              <Typography variant="subtitle1" fontWeight={700} color="text.secondary" gutterBottom>
                INFORMACIÓN DEL EVENTO
              </Typography>
              <Divider sx={{ mb: 2.5 }} />
              <Stack spacing={2.5}>
                <TextField required fullWidth label="Título del evento" name="titulo"
                  value={formData.titulo} onChange={handleInputChange} />
                <TextField required fullWidth label="Descripción" name="descripcion"
                  value={formData.descripcion} onChange={handleInputChange}
                  multiline rows={4} helperText="Describe brevemente de qué trata el evento" />
                <TextField fullWidth label="Detalles adicionales (opcional)" name="detalles"
                  value={formData.detalles} onChange={handleInputChange}
                  multiline rows={3} placeholder="Programa, requisitos, información extra…" />
              </Stack>
            </Paper>

            {/* Fechas y lugar */}
            <Paper sx={{ p: 3, mb: 3, borderRadius: 2 }}>
              <Typography variant="subtitle1" fontWeight={700} color="text.secondary" gutterBottom>
                FECHAS Y LUGAR
              </Typography>
              <Divider sx={{ mb: 2.5 }} />
              <Grid container spacing={2.5}>
                <Grid item xs={12} sm={6}>
                  <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={es}>
                    <DateTimePicker label="Fecha y hora de inicio" value={formData.fechaInicio}
                      onChange={(v) => handleDateChange('fechaInicio', v)}
                      slotProps={{ textField: { fullWidth: true, required: true } }} />
                  </LocalizationProvider>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={es}>
                    <DateTimePicker label="Fecha y hora de fin (opcional)" value={formData.fechaFin}
                      onChange={(v) => handleDateChange('fechaFin', v)}
                      slotProps={{ textField: { fullWidth: true } }} />
                  </LocalizationProvider>
                </Grid>
                <Grid item xs={12}>
                  <TextField fullWidth label="Ubicación" name="ubicacion"
                    value={formData.ubicacion} onChange={handleInputChange}
                    placeholder="Ej: Auditorio Municipal, Calle Mayor 123"
                    InputProps={{ startAdornment: <InputAdornment position="start"><LocationIcon color="action" fontSize="small" /></InputAdornment> }} />
                </Grid>
                <Grid item xs={12}>
                  <TextField fullWidth label="Cómo llegar (opcional)" name="direcciones"
                    value={formData.direcciones} onChange={handleInputChange}
                    multiline rows={2} placeholder="Instrucciones para llegar…" />
                </Grid>
              </Grid>
            </Paper>

            {/* Contacto */}
            <Paper sx={{ p: 3, borderRadius: 2 }}>
              <Typography variant="subtitle1" fontWeight={700} color="text.secondary" gutterBottom>
                ORGANIZADOR Y CONTACTO
              </Typography>
              <Divider sx={{ mb: 2.5 }} />
              <Grid container spacing={2.5}>
                <Grid item xs={12} sm={6}>
                  <TextField fullWidth label="Organizador (opcional)" name="organizador"
                    value={formData.organizador} onChange={handleInputChange}
                    InputProps={{ startAdornment: <InputAdornment position="start"><PersonIcon color="action" fontSize="small" /></InputAdornment> }} />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField fullWidth label="Contacto (opcional)" name="contacto"
                    value={formData.contacto} onChange={handleInputChange}
                    placeholder="Email o teléfono"
                    InputProps={{ startAdornment: <InputAdornment position="start"><PhoneIcon color="action" fontSize="small" /></InputAdornment> }} />
                </Grid>
                <Grid item xs={12}>
                  <TextField fullWidth label="Enlace web (opcional)" name="enlace"
                    value={formData.enlace} onChange={handleInputChange}
                    placeholder="https://…"
                    InputProps={{ startAdornment: <InputAdornment position="start"><LinkIcon color="action" fontSize="small" /></InputAdornment> }} />
                </Grid>
              </Grid>
            </Paper>
          </Grid>

          {/* ══ SIDEBAR ══ */}
          <Grid item xs={12} lg={4}>
            <Box sx={{ position: { lg: 'sticky' }, top: { lg: 16 } }}>

              {/* Acciones */}
              <Paper sx={{ p: 2.5, mb: 2, borderRadius: 2 }}>
                <Button type="submit" variant="contained" fullWidth size="large"
                  startIcon={saving ? <CircularProgress size={18} color="inherit" /> : <SaveIcon />}
                  disabled={saving || fileUpload.uploading} sx={{ mb: 1.5, fontWeight: 700 }}>
                  {saving ? 'Guardando…' : 'Guardar cambios'}
                </Button>
                <Button variant="outlined" fullWidth onClick={() => navigate('/admin/events')}
                  disabled={saving} sx={{ mb: 1 }}>
                  Cancelar
                </Button>
              </Paper>

              {/* Imagen */}
              <Paper sx={{ p: 2.5, mb: 2, borderRadius: 2 }}>
                <Typography variant="subtitle1" fontWeight={700} color="text.secondary" gutterBottom>
                  IMAGEN DEL EVENTO
                </Typography>
                <Divider sx={{ mb: 2 }} />
                {(previewSrc || formData.imagen) && (
                  <Box sx={{ borderRadius: 1.5, overflow: 'hidden', mb: 1.5, bgcolor: '#f5f5f5' }}>
                    <img src={previewSrc || formData.imagen} alt="Vista previa"
                      style={{ width: '100%', maxHeight: 200, objectFit: 'cover', display: 'block' }} />
                  </Box>
                )}
                <Button variant="outlined" component="label" startIcon={<ImageIcon />} fullWidth
                  disabled={fileUpload.uploading}>
                  {fileUpload.uploading
                    ? <><CircularProgress size={16} sx={{ mr: 1 }} /> Subiendo…</>
                    : (formData.imagen ? 'Cambiar imagen' : 'Seleccionar imagen')}
                  <input type="file" accept="image/*" hidden onChange={handleFileChange} />
                </Button>
              </Paper>

              {/* Configuración */}
              <Paper sx={{ p: 2.5, mb: 2, borderRadius: 2 }}>
                <Typography variant="subtitle1" fontWeight={700} color="text.secondary" gutterBottom>
                  CONFIGURACIÓN
                </Typography>
                <Divider sx={{ mb: 2 }} />
                <Stack spacing={2}>
                  <FormControl fullWidth>
                    <InputLabel>Categoría</InputLabel>
                    <Select name="categoria" value={formData.categoria} onChange={handleInputChange} label="Categoría">
                      {categories.map(c => <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>)}
                    </Select>
                  </FormControl>

                  <TextField fullWidth label="Precio (€)" name="precio" type="number"
                    value={formData.precio} onChange={handleInputChange}
                    helperText="Dejar en 0 para gratuito"
                    InputProps={{
                      inputProps: { min: 0, step: 0.01 },
                      startAdornment: <InputAdornment position="start"><EuroIcon fontSize="small" color="action" /></InputAdornment>
                    }} />

                  {Number(formData.precio) > 0 && (
                    <>
                      <TextField fullWidth required label="Entradas disponibles" name="totalpass" type="number"
                        value={formData.totalpass} onChange={handleInputChange}
                        helperText={`Mínimo: ${formData.sold || 0} vendidas`}
                        InputProps={{
                          inputProps: { min: Number(formData.sold) || 0, step: 1 },
                          startAdornment: <InputAdornment position="start"><TicketIcon fontSize="small" color="action" /></InputAdornment>
                        }} />
                      <Box sx={{ p: 1.5, bgcolor: 'info.50', borderRadius: 1 }}>
                        <Typography variant="caption" color="text.secondary">Vendidas: <strong>{formData.sold || 0}</strong></Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ ml: 2 }}>
                          Disponibles: <strong>{Math.max(0, (Number(formData.totalpass) || 0) - (Number(formData.sold) || 0))}</strong>
                        </Typography>
                      </Box>
                    </>
                  )}

                  <Box sx={{
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                    px: 1.5, py: 1, borderRadius: 1,
                    bgcolor: formData.active ? 'success.50' : 'grey.50'
                  }}>
                    <Box>
                      <Typography variant="body2" fontWeight={600}>Evento activo</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {formData.active ? 'Visible en la app' : 'Oculto en la app'}
                      </Typography>
                    </Box>
                    <Switch checked={formData.active} onChange={handleInputChange} name="active" color="success" />
                  </Box>
                </Stack>
              </Paper>

              {/* Zona de peligro */}
              <Paper sx={{ p: 2.5, borderRadius: 2, border: '1px solid', borderColor: 'error.200' }}>
                <Typography variant="subtitle1" fontWeight={700} color="error" gutterBottom>
                  ZONA DE PELIGRO
                </Typography>
                <Divider sx={{ mb: 2, borderColor: 'error.100' }} />
                <Button variant="outlined" color="error" fullWidth startIcon={<DeleteIcon />}
                  onClick={handleDeleteEvent} disabled={deleting}>
                  {deleting ? 'Eliminando…' : 'Eliminar evento'}
                </Button>
              </Paper>

            </Box>
          </Grid>
        </Grid>
      </form>

      {/* Snackbar para mensajes */}
      <Snackbar 
        open={snackbar.open} 
        autoHideDuration={6000} 
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default EventEdit;